// First Created - 19 Aug 2026
// Author - Atul Pandey
// Purpose - To deactivate / archive / hard delete an
//           Org Trail Session Challenge Assignment
// Updated - 19 Aug 2026 - added hard delete option

import React, {
    useState,
} from "react"

import {
    useNavigate,
} from "react-router"

import useTitle from "@/hooks/useTitle"

import EtBtnFormBasic
    from "@/components/common/buttons/EtBtnFormBasic"

import PulseLoader
    from "react-spinners/PulseLoader"

import Modal
    from "@/components/common/modal/Modal"

import {
    useGetOrgTrailSessionChallengeAssignmentsQuery,
    useUpsertOrgTrailSessionChallengeAssignmentMutation,
    useDeleteOrgTrailSessionChallengeAssignmentMutation,
} from "./orgTrailSessionChallengeAssignmentsApiSlice"

/*
|--------------------------------------------------------------------------
| 01 - Resolve Populated Or Plain ID
|--------------------------------------------------------------------------
*/

const getRefId = (
    value
) => {
    if (
        !value
    ) {
        return ""
    }

    if (
        typeof value ===
        "string"
    ) {
        return value
    }

    return (
        value?._id ||
        value?.id ||
        ""
    )
}

/*
|--------------------------------------------------------------------------
| 02 - User Display Name
|--------------------------------------------------------------------------
*/

const getUserDisplayName = (
    user
) => {
    return (
        user?.name ||
        user?.username ||
        user?.email ||
        "User"
    )
}

/*
|--------------------------------------------------------------------------
| 03 - Format Date
|--------------------------------------------------------------------------
*/

const formatDate = (
    value
) => {
    if (
        !value
    ) {
        return "Not defined"
    }

    const date =
        new Date(
            value
        )

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "Not defined"
    }

    return date.toLocaleDateString(
        "en-US",
        {
            month:
                "short",

            day:
                "numeric",

            year:
                "numeric",
        }
    )
}

/*
|--------------------------------------------------------------------------
| Main Component
|--------------------------------------------------------------------------
*/

const EtDeleteOrgTrailSessionChallengeAssignment = ({
    id,
    setActiveAdminView,
}) => {

    // First Created - 19 Aug 2026
    // Author - Atul Pandey
    // Purpose - Deactivate / archive / hard delete assignment

    useTitle(
        "Delete Session Challenge Assignment"
    )

    /*
    |--------------------------------------------------------------------------
    | 04 - Get Assignment From List Cache
    |--------------------------------------------------------------------------
    */

    const {
        assignment,
        isLoading,
        isFetching,
    } =
        useGetOrgTrailSessionChallengeAssignmentsQuery(
            {
                active:
                    true,
            },
            {
                selectFromResult: ({
                    data,
                    isLoading,
                    isFetching,
                }) => ({
                    assignment:
                        data
                            ?.entities[
                            id
                        ],

                    isLoading,

                    isFetching,
                }),
            }
        )

    /*
    |--------------------------------------------------------------------------
    | 05 - Loading
    |--------------------------------------------------------------------------
    */

    if (
        isLoading ||
        isFetching ||
        !assignment
    ) {
        return (
            <PulseLoader
                color={
                    "#ea580c"
                }
            />
        )
    }

    return (
        <EtDeleteOrgTrailSessionChallengeAssignmentForm
            assignment={
                assignment
            }

            id={
                id
            }

            setActiveAdminView={
                setActiveAdminView
            }
        />
    )
}

/*
|--------------------------------------------------------------------------
| Delete Form
|--------------------------------------------------------------------------
*/

const EtDeleteOrgTrailSessionChallengeAssignmentForm = ({
    assignment,
    id,
    setActiveAdminView,
}) => {

    const navigate =
        useNavigate()

    /*
    |--------------------------------------------------------------------------
    | 06 - Modal State
    |--------------------------------------------------------------------------
    */

    const [
        isModalOpen,
        setIsModalOpen,
    ] =
        useState(
            false
        )

    const [
        actionMode,
        setActionMode,
    ] =
        useState(
            "inactive"
        )

    /*
    |--------------------------------------------------------------------------
    | 07 - Update Mutation
    |--------------------------------------------------------------------------
    */

    const [
        upsertOrgTrailSessionChallengeAssignment,
        {
            isLoading:
                isUpdating,

            isError:
                isUpdateError,

            error:
                updateError,
        },
    ] =
        useUpsertOrgTrailSessionChallengeAssignmentMutation()

    /*
    |--------------------------------------------------------------------------
    | 08 - Hard Delete Mutation
    |--------------------------------------------------------------------------
    */

    const [
        deleteOrgTrailSessionChallengeAssignment,
        {
            isLoading:
                isDeleting,

            isError:
                isDeleteError,

            error:
                deleteError,
        },
    ] =
        useDeleteOrgTrailSessionChallengeAssignmentMutation()

    const isLoading =
        isUpdating ||
        isDeleting

    const isError =
        isUpdateError ||
        isDeleteError

    const error =
        updateError ||
        deleteError

    /*
    |--------------------------------------------------------------------------
    | 09 - Assignment Context
    |--------------------------------------------------------------------------
    */

    const orgId =
        getRefId(
            assignment
                ?.orgId
        )

    const trailId =
        getRefId(
            assignment
                ?.trailId
        )

    const sessionId =
        getRefId(
            assignment
                ?.sessionId
        )

    const challengeId =
        getRefId(
            assignment
                ?.challengeId
        )

    const cohortId =
        getRefId(
            assignment
                ?.cohortId
        )

    /*
    |--------------------------------------------------------------------------
    | 10 - Selected Users
    |--------------------------------------------------------------------------
    */

    const selectedUsers =
        Array.isArray(
            assignment
                ?.selectedUserIds
        )
            ?
            assignment
                .selectedUserIds
                .filter(
                    item =>
                        item?.userId &&
                        item?.active !==
                            false
                )
            :
            []

    /*
    |--------------------------------------------------------------------------
    | 11 - Change History
    |--------------------------------------------------------------------------
    */

    const assignmentChanges =
        Array.isArray(
            assignment
                ?.assignmentChangeLog
        )
            ?
            assignment
                .assignmentChangeLog
            :
            []

    /*
    |--------------------------------------------------------------------------
    | 12 - Open Modal
    |--------------------------------------------------------------------------
    */

    const handleOpenModal = (
        mode
    ) => {
        setActionMode(
            mode
        )

        setIsModalOpen(
            true
        )
    }

    /*
    |--------------------------------------------------------------------------
    | 13 - Close Modal
    |--------------------------------------------------------------------------
    */

    const handleCloseModal = () => {
        setIsModalOpen(
            false
        )
    }

    /*
    |--------------------------------------------------------------------------
    | 14 - Return To List
    |--------------------------------------------------------------------------
    */

    const returnToList = () => {
        if (
            setActiveAdminView
        ) {
            setActiveAdminView(
                "orgTrailSessionChallengeAssignments"
            )
        } else {
            navigate(
                "/orgTrailSessionChallengeAssignments"
            )
        }
    }

    /*
    |--------------------------------------------------------------------------
    | 15 - Confirm Action
    |--------------------------------------------------------------------------
    */

    const handleConfirm =
        async (
            e
        ) => {
            e.preventDefault()

            try {

                /*
                |--------------------------------------------------------------------------
                | A - Hard Delete
                |--------------------------------------------------------------------------
                */

                if (
                    actionMode ===
                    "hard"
                ) {
                    const response =
                        await deleteOrgTrailSessionChallengeAssignment({
                            id,
                            deleteMode:
                                "hard",
                        })
                            .unwrap()

                    alert(
                        response
                            ?.message ||
                        "Session Challenge assignment permanently deleted"
                    )

                    returnToList()

                    return
                }

                /*
                |--------------------------------------------------------------------------
                | B - Inactive / Archive
                |--------------------------------------------------------------------------
                */

                const assignmentData = {
                    orgId,

                    trailId,

                    sessionId,

                    challengeId,

                    cohortId,

                    audienceType:
                        assignment
                            ?.audienceType ||
                        "AllCohortUsers",

                    selectedUserIds:
                        assignment
                            ?.audienceType ===
                            "SelectedUsers"
                            ?
                            selectedUsers
                                .map(
                                    item =>
                                        getRefId(
                                            item
                                                ?.userId
                                        )
                                )
                                .filter(
                                    Boolean
                                )
                            :
                            [],

                    assignmentStatus:
                        actionMode ===
                        "archive"
                            ?
                            "Archived"
                            :
                            assignment
                                ?.assignmentStatus ||
                            "Open",

                    availableFrom:
                        assignment
                            ?.availableFrom ||
                        undefined,

                    availableUntil:
                        assignment
                            ?.availableUntil ||
                        undefined,

                    assignmentReason:
                        actionMode ===
                        "archive"
                            ?
                            "Assignment archived by administrator"
                            :
                            "Assignment deactivated by administrator",

                    active:
                        false,
                }

                const response =
                    await upsertOrgTrailSessionChallengeAssignment(
                        assignmentData
                    )
                        .unwrap()

                alert(
                    response
                        ?.message ||
                    (
                        actionMode ===
                        "archive"
                            ?
                            "Session Challenge assignment archived successfully"
                            :
                            "Session Challenge assignment made inactive successfully"
                    )
                )

                returnToList()

            } catch (
                submitError
            ) {
                console.error(
                    submitError
                )

                alert(
                    submitError
                        ?.data
                        ?.message ||
                    (
                        actionMode ===
                        "hard"
                            ?
                            "Failed to permanently delete Session Challenge assignment"
                            :
                            "Failed to update Session Challenge assignment"
                    )
                )
            } finally {
                handleCloseModal()
            }
        }

    /*
    |--------------------------------------------------------------------------
    | 16 - Cancel
    |--------------------------------------------------------------------------
    */

    const handleCancel = () => {
        returnToList()
    }

    /*
    |--------------------------------------------------------------------------
    | 17 - Render
    |--------------------------------------------------------------------------
    */

    return (
        <div
            className="bg-gray-200 p-1 w-3/4 mx-auto mt-5 rounded-lg shadow-md shadow-black"
        >

            {
                isLoading &&
                (
                    <PulseLoader
                        color={
                            "#ea580c"
                        }
                    />
                )
            }

            {
                isError &&
                (
                    <p
                        className="text-orange-700 font-semi-bold py-2"
                    >
                        {
                            error
                                ?.data
                                ?.message
                        }
                    </p>
                )
            }

            <div
                className="bg-white p-1 m-1 rounded"
            >

                <p
                    className="p-1 m-1 font-bold font-poppins text-xl"
                >
                    Delete Session Challenge Assignment
                </p>

                <div
                    className="bg-gray-200 rounded m-1 p-1"
                >

                    <form
                        onSubmit={
                            e => {
                                e.preventDefault()

                                handleOpenModal(
                                    "inactive"
                                )
                            }
                        }

                        className="m-1 p-1 text-xs font-poppins"
                    >

                        {/* 1 - Organization */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Organization
                            </p>

                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.orgId
                                        ?.organizationName ||
                                    "Organization not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />
                        </div>

                        {/* 2 - Trail */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Trail
                            </p>

                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.trailId
                                        ?.trailName ||
                                    "Trail not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />
                        </div>

                        {/* 3 - Cohort */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Cohort
                            </p>

                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.cohortId
                                        ?.cohortName ||
                                    assignment
                                        ?.cohortId
                                        ?.cohortCode ||
                                    "Cohort not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />
                        </div>

                        {/* 4 - Session */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Session
                            </p>

                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.sessionId
                                        ?.sessionName ||
                                    "Session not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />
                        </div>

                        {/* 5 - Challenge */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Session Challenge
                            </p>

                            <input
                                type="text"

                                value={
                                    assignment
                                        ?.challengeId
                                        ?.challengeName ||
                                    "Challenge not defined"
                                }

                                disabled

                                className="w-full rounded p-2 m-1 bg-gray-100"
                            />

                            {
                                assignment
                                    ?.challengeId
                                    ?.challengeDesc &&
                                (
                                    <p
                                        className="text-[9px] text-gray-500 m-1 px-1"
                                    >
                                        {
                                            assignment
                                                .challengeId
                                                .challengeDesc
                                        }
                                    </p>
                                )
                            }
                        </div>

                        {/* 6 - Current Assignment */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Current Assignment
                            </p>

                            <div
                                className="grid grid-cols-1 md:grid-cols-4 gap-1"
                            >

                                <input
                                    type="text"

                                    value={
                                        assignment
                                            ?.audienceType ===
                                            "SelectedUsers"
                                            ?
                                            "Selected Users"
                                            :
                                            "All Cohort Users"
                                    }

                                    disabled

                                    className="w-full rounded p-2 m-1 bg-gray-100"
                                />

                                <input
                                    type="text"

                                    value={
                                        `Selected Users: ${
                                            selectedUsers
                                                .length
                                        }`
                                    }

                                    disabled

                                    className="w-full rounded p-2 m-1 bg-gray-100"
                                />

                                <input
                                    type="text"

                                    value={
                                        `Status: ${
                                            assignment
                                                ?.assignmentStatus ||
                                            "Not defined"
                                        }`
                                    }

                                    disabled

                                    className="w-full rounded p-2 m-1 bg-gray-100"
                                />

                                <input
                                    type="text"

                                    value={
                                        assignment
                                            ?.active !==
                                            false
                                            ?
                                            "Active"
                                            :
                                            "Inactive"
                                    }

                                    disabled

                                    className="w-full rounded p-2 m-1 bg-gray-100"
                                />
                            </div>
                        </div>

                        {/* 7 - Availability */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <p
                                className="font-bold text-xs m-1"
                            >
                                Availability
                            </p>

                            <div
                                className="grid grid-cols-2 gap-1"
                            >

                                <input
                                    type="text"

                                    value={
                                        `From: ${formatDate(
                                            assignment
                                                ?.availableFrom
                                        )}`
                                    }

                                    disabled

                                    className="w-full rounded p-2 m-1 bg-gray-100"
                                />

                                <input
                                    type="text"

                                    value={
                                        `Until: ${formatDate(
                                            assignment
                                                ?.availableUntil
                                        )}`
                                    }

                                    disabled

                                    className="w-full rounded p-2 m-1 bg-gray-100"
                                />
                            </div>
                        </div>

                        {/* 8 - Selected Users */}

                        {
                            assignment
                                ?.audienceType ===
                                "SelectedUsers" &&
                            (
                                <div
                                    className="bg-white rounded m-1 p-2"
                                >

                                    <div
                                        className="flex justify-between items-center"
                                    >

                                        <p
                                            className="font-bold text-xs m-1"
                                        >
                                            Selected Users
                                        </p>

                                        <p
                                            className="text-[10px] bg-orange-100 text-orange-700 rounded-full px-2 py-1"
                                        >
                                            {
                                                selectedUsers
                                                    .length
                                            }
                                        </p>
                                    </div>

                                    <div
                                        className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-72 overflow-y-auto"
                                    >
                                        {
                                            selectedUsers.map(
                                                (
                                                    item,
                                                    index
                                                ) => {

                                                    const user =
                                                        item
                                                            ?.userId

                                                    return (
                                                        <div
                                                            key={
                                                                item
                                                                    ?._id ||
                                                                user
                                                                    ?._id ||
                                                                index
                                                            }

                                                            className="bg-gray-100 rounded m-1 p-2"
                                                        >

                                                            <p
                                                                className="text-[10px] font-bold text-gray-700"
                                                            >
                                                                {
                                                                    getUserDisplayName(
                                                                        user
                                                                    )
                                                                }
                                                            </p>

                                                            {
                                                                user
                                                                    ?.email &&
                                                                (
                                                                    <p
                                                                        className="text-[9px] text-gray-500"
                                                                    >
                                                                        {
                                                                            user
                                                                                .email
                                                                        }
                                                                    </p>
                                                                )
                                                            }
                                                        </div>
                                                    )
                                                }
                                            )
                                        }
                                    </div>
                                </div>
                            )
                        }

                        {/* 9 - Reason */}

                        {
                            assignment
                                ?.assignmentReason &&
                            (
                                <div
                                    className="bg-white rounded m-1 p-2"
                                >

                                    <p
                                        className="font-bold text-xs m-1"
                                    >
                                        Current Assignment Reason
                                    </p>

                                    <div
                                        className="bg-gray-100 rounded m-1 p-2"
                                    >
                                        <p
                                            className="text-[10px] text-gray-600"
                                        >
                                            {
                                                assignment
                                                    .assignmentReason
                                            }
                                        </p>
                                    </div>
                                </div>
                            )
                        }

                        {/* 10 - Change History */}

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            <div
                                className="flex justify-between items-center"
                            >

                                <p
                                    className="font-bold text-xs m-1"
                                >
                                    Assignment Change History
                                </p>

                                <p
                                    className="text-[10px] bg-orange-100 text-orange-700 rounded-full px-2 py-1"
                                >
                                    Changes:{" "}
                                    {
                                        assignmentChanges
                                            .length
                                    }
                                </p>
                            </div>
                        </div>

                        {/* 11 - Warning */}

                        <div
                            className="bg-orange-50 border border-orange-200 rounded m-1 p-2"
                        >

                            <p
                                className="text-xs font-bold text-orange-700"
                            >
                                Choose how this assignment should be removed from active use.
                            </p>

                            <p
                                className="text-[10px] text-orange-700 mt-1"
                            >
                                Make Inactive preserves the current assignment and history.
                                Archive makes the assignment inactive and changes its status to Archived.
                                Hard Delete permanently removes the assignment and its embedded change history.
                            </p>
                        </div>

                        {/* 12 - Actions */}

                        <div
                            id="assignmentActions"

                            className="bg-white m-1 px-1 py-3 rounded"
                        >

                            <EtBtnFormBasic
                                name={
                                    "Make Inactive"
                                }

                                type={
                                    "clear"
                                }

                                handleClick={
                                    () =>
                                        handleOpenModal(
                                            "inactive"
                                        )
                                }
                            />

                            <EtBtnFormBasic
                                name={
                                    "Archive"
                                }

                                type={
                                    "create"
                                }

                                handleClick={
                                    () =>
                                        handleOpenModal(
                                            "archive"
                                        )
                                }
                            />

                            <EtBtnFormBasic
                                name={
                                    "Hard Delete"
                                }

                                type={
                                    "create"
                                }

                                handleClick={
                                    () =>
                                        handleOpenModal(
                                            "hard"
                                        )
                                }
                            />

                            <EtBtnFormBasic
                                name={
                                    "Cancel"
                                }

                                type={
                                    "cancel"
                                }

                                handleClick={
                                    handleCancel
                                }
                            />
                        </div>
                    </form>
                </div>
            </div>

            <Modal
                isOpen={
                    isModalOpen
                }

                onClose={
                    handleCloseModal
                }

                onConfirm={
                    handleConfirm
                }
            />
        </div>
    )
}

export default EtDeleteOrgTrailSessionChallengeAssignment